import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { 
  Box, 
  Button, 
  CircularProgress, 
  FormControl,
  Grid, 
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField, 
  Typography 
} from '@mui/material';
import { RootState, useAppDispatch, useAppSelector } from "../../../../state/state.tsx";
import { closeModal, toggleLoading } from "../../../../state/ModalSlice/ModalSlice.ts";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from 'react-toastify';

interface FormData {
  name: string;
  amount: string;
  date: string;
  method: string;
  description: string;
}

export default function RecordManualPaymentModal() {
  const open = useAppSelector((state: RootState) => state.ModalSlice.RecordManualPaymentModalOpen);
  const isLoading = useAppSelector((state: RootState) => state.ModalSlice.isLoading);
  const dispatch = useAppDispatch();

  const [formData, setFormData] = useState<FormData>({
    name: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    method: 'cash',
    description: ''
  });

  const handleClose = () => {
    dispatch(closeModal("RecordManualPaymentModalOpen"));
    // Reset form data when closing
    setFormData({
      name: '',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      method: 'cash',
      description: ''
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({...formData, [e.target.name]: e.target.value});
  };

  const handleSelectChange = (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    const name = e.target.name as string;
    const value = e.target.value as string;
    setFormData({...formData, [name]: value});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate amount (should be a positive number)
    if (isNaN(Number(formData.amount)) || Number(formData.amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    try {
      dispatch(toggleLoading(true));
      
      // TODO: Replace with actual API call to record payment
      // Example: await api.recordPayment({ ...formData, type: 'received' });
      
      // Simulate API call with timeout
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast.success("Payment recorded successfully!");
      handleClose();
    } catch (error) {
      toast.error("Failed to record payment. Please try again.");
      console.error("Error recording payment:", error);
    } finally {
      dispatch(toggleLoading(false));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '10px',
        },
      }}
    >
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          p: 2,
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
        }}
      >
        <Typography variant="h6">Record Manual Payment</Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>
      
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="Received From"
                name="name"
                value={formData.name}
                onChange={handleChange}
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Amount (₹)"
                name="amount"
                type="number"
                value={formData.amount}
                onChange={handleChange}
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Date"
                name="date"
                type="date"
                value={formData.date}
                onChange={handleChange}
                disabled={isLoading}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required disabled={isLoading}>
                <InputLabel id="payment-method-label">Payment Method</InputLabel>
                <Select
                  labelId="payment-method-label"
                  id="payment-method"
                  name="method"
                  value={formData.method}
                  label="Payment Method"
                  onChange={handleSelectChange as any}
                >
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                  <MenuItem value="upi">UPI</MenuItem>
                  <MenuItem value="card">Card Payment</MenuItem>
                  <MenuItem value="cheque">Cheque</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                multiline
                rows={2}
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={isLoading}
                startIcon={isLoading && <CircularProgress size={24} color="inherit" />}
              >
                {isLoading ? 'Recording...' : 'Record Payment'}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  );
} 